import { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  X, ArrowLeft, ArrowRight, Search, Plus, Trash2, Check,
  ChevronDown, ChevronRight, Printer, FileText, FlaskConical,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { useAuthStore } from '@/store/auth.store';
import { Cie11Search, type Cie11Result } from '@/components/cie11/Cie11Search';
import {
  TAMANO_INFO, DISENO_INFO, getDimensiones,
  type TemplateData, type EstudioItem,
} from '../configuracion/templates/template-types';
import { TemplateRenderer } from '../configuracion/templates/TemplateRenderer';
import { usePlantillasConfig, DEFAULT_PLANTILLAS_CONFIG } from '../configuracion/templates/usePlantillaConfig';
import catalogoJson from '@/assets/json/estudios.json';
import { getErrorMessage } from '@/lib/errors';

// ─── Types ───────────────────────────────────────────────────────────────────

interface WizardForm {
  estudios: EstudioItem[];
  cie11: Cie11Result | null;
  indicaciones: string;
}

type StepId = 'estudios' | 'cabecera' | 'resumen';
const STEP_LABEL: Record<StepId, string> = {
  estudios: 'Estudios',
  cabecera: 'Indicación',
  resumen:  'Resumen',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';

function formatDate(d: string | Date) {
  return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

function toDisplayName(p: any): string {
  if (!p) return '';
  return [p.nombre, p.apellidoPaterno, p.apellidoMaterno].filter(Boolean).join(' ');
}


// ─── Catálogo de estudios ─────────────────────────────────────────────────────

interface CatalogoStepProps {
  selected: EstudioItem[];
  onToggle: (estudio: EstudioItem) => void;
  onAddCustom: (nombre: string) => void;
}

function CatalogoStep({ selected, onToggle, onAddCustom }: CatalogoStepProps) {
  const [searchQ, setSearchQ] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [customName, setCustomName] = useState('');

  const selectedNames = new Set(selected.map(e => e.nombre));

  const categorias = (catalogoJson.categorias as { nombre: string; estudios: EstudioItem[] }[]);
  const filtered = searchQ.trim()
    ? categorias.map(c => ({
        ...c,
        estudios: c.estudios.filter(e =>
          e.nombre.toLowerCase().includes(searchQ.toLowerCase()) ||
          e.descripcion?.toLowerCase().includes(searchQ.toLowerCase())
        ),
      })).filter(c => c.estudios.length > 0)
    : categorias;

  function toggleCategory(nombre: string) {
    setExpanded(prev => {
      const s = new Set(prev);
      s.has(nombre) ? s.delete(nombre) : s.add(nombre);
      return s;
    });
  }

  function handleAddCustom() {
    const name = customName.trim();
    if (!name) return;
    onAddCustom(name);
    setCustomName('');
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
        <input
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
          placeholder="Buscar estudio..."
          className={`${inputCls} pl-8`}
        />
      </div>

      {/* Selected count */}
      {selected.length > 0 && (
        <div className="bg-primary-50 border border-primary-200 rounded-xl px-3 py-2">
          <p className="text-xs text-primary-700 font-medium">
            {selected.length} estudio{selected.length !== 1 ? 's' : ''} seleccionado{selected.length !== 1 ? 's' : ''}
          </p>
          <div className="flex flex-wrap gap-1 mt-1.5">
            {selected.map((e, i) => (
              <span key={i}
                onClick={() => onToggle(e)}
                className="bg-white border border-primary-200 text-primary-700 text-[10px] px-2 py-0.5 rounded-full cursor-pointer hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors">
                {e.nombre} ×
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Catalog */}
      <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
        {filtered.map(cat => {
          const isOpen = searchQ.trim() ? true : expanded.has(cat.nombre);
          return (
            <div key={cat.nombre} className="border border-gray-100 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => toggleCategory(cat.nombre)}
                className="flex items-center justify-between w-full px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left">
                <span className="text-xs font-semibold text-text-primary">{cat.nombre}</span>
                <div className="flex items-center gap-2">
                  {cat.estudios.some(e => selectedNames.has(e.nombre)) && (
                    <span className="w-4 h-4 rounded-full bg-primary-600 text-white text-[9px] font-bold flex items-center justify-center">
                      {cat.estudios.filter(e => selectedNames.has(e.nombre)).length}
                    </span>
                  )}
                  {isOpen ? <ChevronDown size={13} className="text-text-secondary" /> : <ChevronRight size={13} className="text-text-secondary" />}
                </div>
              </button>
              {isOpen && (
                <div className="divide-y divide-gray-50">
                  {cat.estudios.map(estudio => {
                    const isSelected = selectedNames.has(estudio.nombre);
                    return (
                      <button
                        key={estudio.nombre}
                        type="button"
                        onClick={() => onToggle(estudio)}
                        className={cn(
                          'flex items-start gap-3 w-full px-3 py-2.5 text-left transition-colors',
                          isSelected ? 'bg-primary-50' : 'bg-white hover:bg-gray-50'
                        )}>
                        <span className={cn(
                          'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 mt-0.5',
                          isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300'
                        )}>
                          {isSelected && <Check size={9} className="text-white" />}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-text-primary leading-tight">{estudio.nombre}</p>
                          {estudio.descripcion && (
                            <p className="text-[10px] text-text-secondary mt-0.5">{estudio.descripcion}</p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Custom study */}
      <div className="border border-dashed border-gray-300 rounded-xl p-3 space-y-2">
        <p className="text-xs text-text-secondary font-medium">Agregar estudio personalizado</p>
        <div className="flex gap-2">
          <input
            value={customName}
            onChange={e => setCustomName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddCustom()}
            placeholder="Nombre del estudio..."
            className={`${inputCls} flex-1`}
          />
          <button
            type="button"
            onClick={handleAddCustom}
            disabled={!customName.trim()}
            className="flex items-center gap-1 bg-primary-600 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-primary-700 disabled:opacity-40 transition-colors flex-shrink-0">
            <Plus size={12} /> Agregar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  pacienteId: string;
  solicitudId?: string | null;
  paciente?: any;
  onClose: () => void;
}

const STEPS: StepId[] = ['estudios', 'cabecera', 'resumen'];

export function EstudioWizard({ pacienteId, solicitudId: initialId, paciente, onClose }: Props) {
  const { usuario } = useAuthStore();
  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState<WizardForm>({
    estudios: [],
    cie11: null,
    indicaciones: '',
  });
  const [savedId, setSavedId] = useState<string | null>(initialId || null);
  const printRef = useRef<HTMLDivElement>(null);

  const { data: existingSolicitud } = useQuery({
    queryKey: ['solicitud-estudio', initialId],
    queryFn: () => api.get(`/api/estudios/${initialId}`).then(r => r.data),
    enabled: !!initialId,
  });

  // Load existing draft
  useEffect(() => {
    if (existingSolicitud) {
      setForm({
        estudios: existingSolicitud.estudios || [],
        cie11: existingSolicitud.codigoCie11 ? {
          id:      existingSolicitud.idEntidadCie11 || '',
          theCode: existingSolicitud.codigoCie11,
          title:   existingSolicitud.tituloCie11 || '',
        } : null,
        indicaciones: existingSolicitud.indicaciones || '',
      });
    }
  }, [existingSolicitud]);

  const { data: clinica } = useQuery({
    queryKey: ['clinica', usuario?.clinicaId],
    queryFn: () => api.get(`/api/clinicas/${usuario!.clinicaId}`).then(r => r.data),
    enabled: !!usuario?.clinicaId,
  });

  const saveMutation = useMutation({
    mutationFn: (dto: any) => savedId
      ? api.patch(`/api/estudios/${savedId}`, dto).then(r => r.data)
      : api.post('/api/estudios', dto).then(r => r.data),
    onSuccess: (data) => { if (data?.id) setSavedId(data.id); },
  });

  const activarMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/estudios/${id}/activar`).then(r => r.data),
    onSuccess: () => { toast.success('Solicitud emitida'); onClose(); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al emitir la solicitud')),
  });

  const currentStep = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;

  function buildDto(status?: string) {
    return {
      pacienteId,
      clinicaId:     usuario?.clinicaId,
      medicoId:      usuario?.id,
      codigoCie11:   form.cie11?.theCode   || null,
      tituloCie11:   form.cie11?.title     || null,
      idEntidadCie11: form.cie11?.id       || null,
      indicaciones:  form.indicaciones     || null,
      status:        status || 'borrador',
      estudios:      form.estudios,
    };
  }

  async function saveDraft() {
    try {
      await saveMutation.mutateAsync(buildDto('borrador'));
      toast.success('Guardado como borrador');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al guardar borrador'));
    }
  }

  async function handleEmitir() {
    try {
      const dto = buildDto('borrador');
      let id = savedId;
      if (!id) {
        const created = await saveMutation.mutateAsync(dto);
        id = created.id;
      } else {
        await saveMutation.mutateAsync(dto);
      }
      if (id) activarMutation.mutate(id);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al guardar antes de emitir'));
    }
  }

  function toggleEstudio(estudio: EstudioItem) {
    setForm(prev => {
      const exists = prev.estudios.some(e => e.nombre === estudio.nombre);
      return {
        ...prev,
        estudios: exists
          ? prev.estudios.filter(e => e.nombre !== estudio.nombre)
          : [...prev.estudios, estudio],
      };
    });
  }

  function addCustomEstudio(nombre: string) {
    if (form.estudios.some(e => e.nombre === nombre)) {
      toast.info('Este estudio ya está en la lista');
      return;
    }
    setForm(prev => ({ ...prev, estudios: [...prev.estudios, { nombre }] }));
  }

  function removeEstudio(idx: number) {
    setForm(prev => ({ ...prev, estudios: prev.estudios.filter((_, i) => i !== idx) }));
  }

  function validateStep(): boolean {
    if (currentStep === 'estudios' && form.estudios.length === 0) {
      toast.error('Selecciona al menos un estudio');
      return false;
    }
    return true;
  }

  function next() { if (validateStep()) setStepIdx(i => Math.min(i + 1, STEPS.length - 1)); }
  function prev() { setStepIdx(i => Math.max(i - 1, 0)); }

  // Print preview
  const { data: plantillasConfig } = usePlantillasConfig();
  const plantillaCfg = (plantillasConfig ?? DEFAULT_PLANTILLAS_CONFIG).laboratorio;
  const { width: pw, height: ph, cssSize } = getDimensiones(plantillaCfg.tamanoHoja, plantillaCfg.orientacion);
  const PREVIEW_SCALE = 0.55;
  const previewW = Math.round(pw * PREVIEW_SCALE);
  const previewH = Math.round(ph * PREVIEW_SCALE);

  const solicitudFolio = existingSolicitud?.folio;
  const templateData: TemplateData = {
    tipo: 'laboratorio',
    folio: solicitudFolio || '—',
    fecha: new Date().toLocaleDateString('es-MX'),
    qrUrl: solicitudFolio ? `${window.location.origin}/estudio-digital/${solicitudFolio}` : undefined,
    clinica: {
      nombre:    (clinica as any)?.nombre    || 'Clínica',
      cedula:    (clinica as any)?.cedulaFiscal || '',
      direccion: (clinica as any)?.direccion || '',
      telefono:  (clinica as any)?.telefono  || '',
    },
    medico: {
      nombre:       (usuario as any)?.nombre       || 'Médico',
      cedula:       (usuario as any)?.cedula        || '',
      especialidad: (usuario as any)?.especialidad  || '',
    },
    paciente: {
      nombre: toDisplayName(paciente),
      fechaNacimiento: paciente?.fechaNacimiento ? formatDate(paciente.fechaNacimiento) : '',
      sexo: paciente?.genero ? ({ masculino: 'Masculino', femenino: 'Femenino', otro: 'Otro' }[paciente.genero as string] || '') : '',
    },
    fechaConsulta: new Date().toLocaleDateString('es-MX'),
    estudios: form.estudios,
    instrucciones: form.indicaciones || undefined,
  };

  function handlePrint() {
    const el = printRef.current;
    if (!el) return;
    const css = `*{margin:0;padding:0;box-sizing:border-box}body{background:#fff}@media print{@page{size:${cssSize};margin:0}}`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><style>${css}</style></head><body>${DOMPurify.sanitize(el.innerHTML)}</body></html>`);
    w.document.close();
    w.onload = () => w.print();
  }

  // ─── Step content ───────────────────────────────────────────────────────────

  function renderStep() {
    switch (currentStep) {
      case 'estudios': return (
        <CatalogoStep
          selected={form.estudios}
          onToggle={toggleEstudio}
          onAddCustom={addCustomEstudio}
        />
      );

      case 'cabecera': return (
        <div className="space-y-5">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Diagnóstico CIE-11 <span className="font-normal">(opcional)</span>
            </label>
            <Cie11Search
              value={form.cie11}
              onChange={v => setForm(p => ({ ...p, cie11: v }))}
              placeholder="Buscar diagnóstico (mín. 3 caracteres)..."
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">
              Indicaciones clínicas <span className="font-normal">(opcional)</span>
            </label>
            <textarea
              rows={4}
              className={`${inputCls} resize-none`}
              value={form.indicaciones}
              onChange={e => setForm(p => ({ ...p, indicaciones: e.target.value }))}
              placeholder="Motivo de solicitud, condiciones de ayuno, instrucciones al paciente..."
            />
          </div>
        </div>
      );

      case 'resumen': return (
        <div className="space-y-5">
          {/* Studies list */}
          <div className="bg-gray-50 rounded-xl border border-gray-100 p-4 space-y-2">
            <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3">
              Estudios solicitados ({form.estudios.length})
            </p>
            {form.estudios.map((e, i) => (
              <div key={i} className="flex items-start gap-2.5 py-1.5 border-b border-gray-100 last:border-0">
                <span className="w-5 h-5 rounded-full bg-primary-50 border border-primary-200 text-primary-700 text-[9px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-text-primary leading-tight">{e.nombre}</p>
                  {e.descripcion && <p className="text-[10px] text-text-secondary">{e.descripcion}</p>}
                </div>
                <button type="button" onClick={() => removeEstudio(i)}
                  className="p-1 rounded hover:bg-red-50 text-text-secondary hover:text-red-500 transition-colors flex-shrink-0">
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>

          {/* CIE-11 */}
          {form.cie11 && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl px-3 py-2">
              <p className="text-[10px] text-blue-500 font-semibold uppercase tracking-wide mb-0.5">Diagnóstico CIE-11</p>
              <p className="text-xs font-medium text-blue-800">{form.cie11.theCode} — {form.cie11.title}</p>
            </div>
          )}

          {/* Indicaciones */}
          {form.indicaciones && (
            <div className="bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
              <p className="text-[10px] text-text-secondary font-semibold uppercase tracking-wide mb-0.5">Indicaciones</p>
              <p className="text-xs text-text-primary">{form.indicaciones}</p>
            </div>
          )}

          {/* Template label */}
          <div className="flex items-center justify-between text-xs text-text-secondary bg-gray-50 px-3 py-2 rounded-lg">
            <span>Plantilla: <strong className="text-text-primary">{DISENO_INFO[plantillaCfg.diseno].label}</strong></span>
            <span>{TAMANO_INFO[plantillaCfg.tamanoHoja].label}</span>
          </div>

          {/* Print preview */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Vista previa</p>
              <button onClick={handlePrint}
                className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-800 transition-colors">
                <Printer size={12} /> Imprimir
              </button>
            </div>
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50 flex justify-center py-4">
              <div style={{ width: previewW, height: previewH, overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.12)', borderRadius: 4 }}>
                <div ref={printRef} style={{ width: pw, height: ph, transform: `scale(${PREVIEW_SCALE})`, transformOrigin: 'top left', pointerEvents: 'none', userSelect: 'none' }}>
                  <TemplateRenderer data={templateData} diseno={plantillaCfg.diseno} width={pw} height={ph} disenoPersonalizadoHtml={plantillaCfg.disenoPersonalizadoHtml} />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  const isSaving   = saveMutation.isPending;
  const isEmitting = activarMutation.isPending;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <h1 className="font-semibold text-text-primary text-sm">
            {initialId ? 'Editar solicitud' : 'Nueva solicitud de estudio'} · {toDisplayName(paciente)}
          </h1>
          <div className="flex items-center gap-2">
            <button onClick={saveDraft} disabled={isSaving}
              className="flex items-center gap-1.5 text-xs border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 text-text-secondary disabled:opacity-50 transition-colors">
              <FileText size={12} /> {isSaving ? 'Guardando...' : 'Guardar borrador'}
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Step indicator */}
        <div className="px-5 py-2.5 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-0">
            {STEPS.map((step, i) => {
              const isActive    = i === stepIdx;
              const isCompleted = i < stepIdx;
              return (
                <div key={step} className="flex items-center flex-shrink-0">
                  <button
                    onClick={() => i < stepIdx && setStepIdx(i)}
                    disabled={i >= stepIdx}
                    className={cn('flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium transition-all',
                      isActive    ? 'text-primary-700 bg-primary-50 border border-primary-200' :
                      isCompleted ? 'text-green-700 cursor-pointer hover:bg-green-50'           :
                                    'text-text-secondary'
                    )}>
                    <span className={cn('w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0',
                      isActive    ? 'bg-primary-600 text-white' :
                      isCompleted ? 'bg-green-500 text-white'   :
                                    'bg-gray-200 text-gray-500'
                    )}>
                      {isCompleted ? <Check size={9} /> : i + 1}
                    </span>
                    {STEP_LABEL[step]}
                  </button>
                  {i < STEPS.length - 1 && <ChevronRight size={13} className="mx-1 text-gray-300 flex-shrink-0" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <h2 className="text-base font-semibold text-text-primary mb-4">{STEP_LABEL[currentStep]}</h2>
          {renderStep()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3.5 border-t border-gray-100 bg-white">
          <button onClick={prev} disabled={stepIdx === 0}
            className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors disabled:opacity-30">
            <ArrowLeft size={14} /> Anterior
          </button>

          {isLast ? (
            <div className="flex items-center gap-3">
              <button onClick={saveDraft} disabled={isSaving}
                className="text-sm border border-gray-200 px-4 py-2 rounded-lg hover:bg-gray-50 text-text-secondary disabled:opacity-50 transition-colors">
                Guardar borrador
              </button>
              <button onClick={handleEmitir} disabled={isSaving || isEmitting}
                className="flex items-center gap-1.5 text-sm bg-primary-600 text-white px-5 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors font-medium">
                <FlaskConical size={14} />
                {isEmitting ? 'Emitiendo...' : 'Emitir solicitud'}
              </button>
            </div>
          ) : (
            <button onClick={next}
              className="flex items-center gap-1.5 text-sm bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors font-medium">
              Siguiente <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
